import { KeyRound, MessageSquareText, Plug } from '@orgus/ui/icons';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { ErrorState, Panel, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger } from '@orgus/ui';
import { CopyBlock } from './CopyBlock.tsx';
import { useConnectInfo } from './useAiData.ts';

function Step({ n, icon, title, children }: { n: number; icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full border border-border bg-raised text-xs font-medium text-fg-muted num" aria-hidden>
        {n}
      </span>
      <div className="min-w-0 flex-1 pb-1">
        <p className="flex items-center gap-1.5 font-medium text-fg [&_svg]:size-4 [&_svg]:text-fg-subtle">
          {icon}
          {title}
        </p>
        <div className="mt-1.5 flex flex-col gap-2 text-sm text-fg-muted">{children}</div>
      </div>
    </li>
  );
}

const EXAMPLES = ['What needs me right now?', 'Summarise what my sessions did in the last hour.', 'Which pipeline items failed today, and why?'];

/** How to connect Claude Code (or any MCP client) to this hub as an AI agent. */
export function ConnectPanel() {
  const { load, retry } = useConnectInfo();
  const [tab, setTab] = useState<'json' | 'command'>('json');
  return (
    // A guide, not the page: it folds so the agents and their activity come first.
    <Panel title="Connect an agent" sub="3 steps" icon={<Plug />} collapsible>
      <ol className="flex flex-col gap-5">
        <Step n={1} icon={<KeyRound />} title="Create an agent token">
          <p>
            In API tokens. It signs in as <code className="font-mono text-fg">agent:&lt;name&gt;</code> and reaches the operate tier; give it only{' '}
            <code className="font-mono text-fg">read</code> for an agent that watches.
          </p>
        </Step>
        <Step n={2} icon={<Plug />} title="Add Orgus to Claude Code">
          {load.status === 'loading' ? (
            <div role="status" aria-label="Loading the connection details" className="flex flex-col gap-2">
              <Skeleton className="h-10 w-64" />
              <Skeleton className="h-36" />
            </div>
          ) : load.status === 'error' ? (
            <ErrorState error={load.error} onRetry={retry} variant="inline" />
          ) : (
            <Tabs value={tab} onValueChange={(v) => setTab(v as 'json' | 'command')}>
              <TabsList aria-label="How to add the MCP server">
                <TabsTrigger value="json">.mcp.json</TabsTrigger>
                <TabsTrigger value="command">One command</TabsTrigger>
              </TabsList>
              <TabsContent value="json" className="flex flex-col gap-2 pt-3">
                <p>
                  Add this to the project's <code className="font-mono text-fg">.mcp.json</code>, then set{' '}
                  <code className="font-mono text-fg">ORGUS_TOKEN</code> in your environment. The token stays out of the file, so it is safe to commit.
                </p>
                <CopyBlock text={load.data.mcpJson} label="MCP config" multiline />
              </TabsContent>
              <TabsContent value="command" className="flex flex-col gap-2 pt-3">
                <p>
                  Registers Orgus for you only. Replace <code className="font-mono text-fg">{load.data.tokenPlaceholder}</code> with the token; Claude
                  Code keeps it in your user config.
                </p>
                <CopyBlock text={load.data.claudeCommand} label="Command" />
              </TabsContent>
            </Tabs>
          )}
        </Step>
        <Step n={3} icon={<MessageSquareText />} title="Ask it something">
          <p>Start Claude Code in any folder and ask, for example:</p>
          <ul className="flex flex-wrap gap-2">
            {EXAMPLES.map((e) => (
              <li key={e} className="rounded-md border border-border-subtle bg-raised px-2 py-1 text-fg">
                {e}
              </li>
            ))}
          </ul>
          <p>Its calls show up below as they happen.</p>
        </Step>
      </ol>
    </Panel>
  );
}
